import { cn } from "@/lib/utils";
import {
  MessageEntity,
  // ReactionSendingStatus
} from "@/indexedDB/types";
// import { toast } from "@/hooks/useToast";
// import { postReactionMessageToChat } from "@/middleware/reactions";
// import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

import ReactionDialog from "./ReactionsDialog";
/**
 * Sorts message reactions alphabetically by emoji
 *
 * @param reactions - Object containing reaction counts with emoji as keys
 * @returns Sorted array of [emoji, count] pairs
 */
export function getSortedReactions(
  reactions: Record<string, number> = {},
): [string, number][] {
  return Object.entries(reactions).sort(([a], [b]) => a.localeCompare(b));
}

function sumReactionCount(reactions?: Record<string, number>): number {
  if (!reactions) {
    return 0;
  }
  return Object.values(reactions).reduce((sum, count) => {
    return sum + (typeof count === "number" && !isNaN(count) ? count : 0);
  }, 0);
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  parentMessage: MessageEntity;
}

export default function MessageReactions({ parentMessage, className }: Props) {
  // const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState<"show-reaction-details" | null>(
    null,
  );

  // momeoized values for reactions, reactionCount, hasUserReacted
  const { reactions, reactionCount, hasUserReacted } = useMemo(() => {
    const sortedReactions = getSortedReactions(parentMessage.reactions);
    const count = sumReactionCount(parentMessage.reactions);
    const userReacted = !!parentMessage.userReaction;

    return {
      reactions: sortedReactions,
      reactionCount: count,
      hasUserReacted: userReacted,
    };
  }, [parentMessage.reactions, parentMessage.userReaction]);

  // close dialog  when reactions become empty
  useEffect(() => {
    if (reactionCount === 0 || reactions.length === 0) {
      setOpenDialog(null);
    }
  }, [reactionCount, reactions.length]);

  if (!reactions.length || reactionCount === 0) {
    return null;
  }

  const handleClick = () => {
    setOpenDialog("show-reaction-details");
  };

  return (
    <>
      <div
        // onClick={() => setOpenDialog("show-reaction-details")}
        onClick={handleClick}
        className={cn("lg:cursor-pointer", className)}
        role="button"
        aria-label={`View ${reactionCount} reaction${reactionCount === 1 ? "" : "s"}`}
        tabIndex={0}
      >
        <div className="contents">
          <div
            className={cn(
              "flex items-center gap-1 rounded-b-xl border border-border bg-transparent px-2 py-1 align-middle text-sm text-muted-foreground",
              parentMessage.isOutgoing ? "rounded-tl-xl" : "rounded-tr-xl",
              // TO DO maybe we need to highlight if user has reacted
              hasUserReacted && "border-primary bg-secondary/10",
            )}
          >
            {reactions.map(([reaction]) => {
              return (
                <div key={reaction} className="text-xs">
                  {reaction}
                </div>
              );
            })}

            <span className="ml-1">
              {/* 99+ */}
              {reactionCount > 999 ? "999+" : reactionCount || ""}
            </span>
          </div>
        </div>
      </div>

      {reactions.length > 0 && (
        <ReactionDialog
          open={openDialog === "show-reaction-details"}
          onOpenClose={(state) =>
            setOpenDialog(state ? "show-reaction-details" : null)
          }
          chatId={parentMessage.chatId}
          messageId={parentMessage.id}
          reactions={parentMessage.reactions}
        />
      )}
    </>
  );
}
